import { useState } from 'react';

/**
 * One-time password reveal panel shared by Add User and Reset Password flows.
 *
 * Renders a readonly monospace input containing the server-generated password
 * alongside a Copy button (clipboard API with execCommand fallback) and a
 * single "I've saved it, close" exit. This component does NOT own a Dialog —
 * callers wrap it in a `<Dialog dismissible={false}>` so the only way out is
 * the explicit confirm button.
 *
 * The `testIdPrefix` lets consumers namespace their data-testids (e.g.
 * `add-user-reveal` vs `reset-password-reveal`) without colliding when both
 * dialogs exist on the page during teardown.
 */
export interface PasswordRevealPanelProps {
  email: string;
  password: string;
  onConfirm: () => void;
  /** Prefix for data-testid attributes. Default: `password-reveal`. */
  testIdPrefix?: string;
}

export function PasswordRevealPanel({
  email,
  password,
  onConfirm,
  testIdPrefix = 'password-reveal',
}: PasswordRevealPanelProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(password);
        ok = true;
      }
    } catch {
      // fall through to execCommand fallback
    }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = password;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }
  };

  const inputId = `${testIdPrefix}-input`;

  return (
    <div className="space-y-4" data-testid={`${testIdPrefix}-panel`}>
      <p className="text-sm text-gray-300">
        Share this password with <span className="font-semibold text-white">{email}</span> via a
        secure channel. It will not be shown again.
      </p>

      <div>
        <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-gray-200">
          New password
        </label>
        <div className="flex gap-2">
          <input
            id={inputId}
            data-testid={`${testIdPrefix}-password-input`}
            type="text"
            readOnly
            value={password}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded border border-gray-600 bg-gray-900 px-3 py-2 font-mono text-sm text-gray-100"
          />
          <button
            type="button"
            data-testid={`${testIdPrefix}-copy-button`}
            onClick={handleCopy}
            className={`rounded px-3 py-2 text-sm font-medium text-white transition-colors ${
              copied ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
            }`}
            aria-live="polite"
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          data-testid={`${testIdPrefix}-close-button`}
          onClick={onConfirm}
          className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
        >
          I&apos;ve saved it, close
        </button>
      </div>
    </div>
  );
}
